import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Alert,
  InputGroup,
  Pagination,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import {
  Plus,
  Search,
  Eye,
  ShoppingCart,
  DollarSign,
  Calendar,
  User,
  CreditCard,
  Download,
  AlertCircle,
  Phone,
  FileText,
  Printer,
  Check,
  Minus,
  Package,
  X,
  Clock, // ✅ AJOUT: X et Clock
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useBoutique } from "../../contexts/BoutiqueContext";
import saleService from "../../services/saleService";
import TicketPrintService from "../../services/ticketPrintService";
import InvoiceGeneratorService from "../../services/InvoiceGeneratorService";
import { useNavigate, useLocation} from "react-router-dom";
import { canShowCredits } from "../../utils/creditHelper";
import ExpenseManager from "./ExpenseManager";
import expenseService from "../../services/expenseService";
import CustomDateFilter from "./CustomDateFilter";

const SalesList = () => {
  const { user, isManager } = useAuth();
  const { currentBoutique } = useBoutique();
  const navigate = useNavigate();
  const location = useLocation();
  const [expenses, setExpenses] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [customDateFilter, setCustomDateFilter] = useState(null);

  // États principaux
  const [filteredSales, setFilteredSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // ✅ Liste des méthodes de paiement pour les filtres (sans MIXED)
  const paymentMethods = [
    { value: "CASH", label: "Espèces", icon: "💵" },
    { value: "WAVE", label: "Wave", icon: "📱" },
    { value: "ORANGE_MONEY", label: "Orange Money", icon: "🍊" },
    { value: "BANK_TRANSFER", label: "Virement bancaire", icon: "🏦" },
    { value: "CHECK", label: "Chèque", icon: "📋" },
    { value: "CREDIT", label: "Crédit", icon: "💳" },
    { value: "CARD", label: "Carte bancaire", icon: "💳" },
  ];

  // États des modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  const [showCancelled, setShowCancelled] = useState(false);

  // ✅ AJOUTER ces états (après les autres useState)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [saleToCancel, setSaleToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Ajoutez un état pour le solde caisse
  const [cashBalanceData, setCashBalanceData] = useState({
    cashIncome: 0,
    totalExpenses: 0,
    netBalance: 0,
  });

  // États des filtres et recherche
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("today");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("saleDate");
  const [sortOrder, setSortOrder] = useState("desc");

  // États de pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // ✅ 10 par défaut
  const pageSizeOptions = [10, 15, 20, 30, 50];

  // États pour filtres
  const [filterCounts, setFilterCounts] = useState({});

   // ✅ NOUVEAUX ÉTATS pour pagination serveur
  const [salesData, setSalesData] = useState([]); // Ventes de la page courante
  const [serverPagination, setServerPagination] = useState({
    currentPage: 0,
    totalPages: 1,
    totalElements: 0,
    pageSize: 20,
    hasNext: false,
    hasPrevious: false,
  });
  const [serverStats, setServerStats] = useState({
    filterCounts: { total: 0, today: 0, week: 0, month: 0, year: 0, paid: 0, credit: 0, creditPaid: 0, todayRevenue: 0 },
    periodStats: { salesCount: 0, revenue: 0 },
    paymentMethods: {},
  });
  const [searchDebounce, setSearchDebounce] = useState(null);

  // États pour l'impression
  const [printing, setPrinting] = useState(null);

  const calculateCashBalance = (sales) => {
    const today = new Date();
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);

    return sales
      .filter((sale) => {
        const saleDate = new Date(
          sale.createdAt || sale.created_at || sale.saleDate
        );
        const isTodaySale = saleDate >= todayStart && saleDate <= todayEnd;
        const paymentMethod = sale.paymentMethod || sale.payment_method;
        const isCashPayment = paymentMethod === "CASH";

        // Ventes directes en espèces aujourd'hui
        if (isTodaySale && isCashPayment && !sale.isCredit && !sale.is_credit) {
          return true;
        }

        // Paiements de crédit en espèces aujourd'hui
        if (sale.isCredit || sale.is_credit) {
          const isCreditPaid = sale.isCreditPaid || sale.is_credit_paid;
          const creditPaymentMethod =
            sale.creditPaymentMethod || sale.credit_payment_method;
          const paymentDate = new Date(
            sale.creditPaymentDate ||
              sale.credit_payment_date ||
              sale.lastPaymentDate ||
              sale.updatedAt ||
              sale.updated_at
          );

          const isPaymentToday =
            paymentDate >= todayStart && paymentDate <= todayEnd;
          const isCashCreditPayment =
            creditPaymentMethod === "CASH" ||
            (isCreditPaid && paymentMethod === "CASH");

          return isPaymentToday && isCashCreditPayment;
        }

        return false;
      })
      .reduce((total, sale) => {
        if (!sale.isCredit && !sale.is_credit) {
          return total + (sale.total || 0);
        }

        const creditPaid = sale.creditPaid || sale.credit_paid || 0;
        return total + Math.min(creditPaid, sale.total || 0);
      }, 0);
  };

  // ✅ Chargement initial
  useEffect(() => {
    loadSales(0);
    loadExpenses();
    loadCashBalance(); // ✅ Charger la caisse dès le départ
  }, [user, location.key]);

  // ✅ Recharger le solde caisse quand les dépenses changent
  useEffect(() => {
    loadCashBalance();
  }, [expenses]);

  // ✅ NOUVEAU : Recharger les ventes quand un filtre ou la taille de page change
  // (sauf searchTerm qui utilise le debounce)
  useEffect(() => {
    loadSales(0);
  }, [dateFilter, paymentFilter, statusFilter, showCancelled, customDateFilter, itemsPerPage]);


  // ✅ FONCTION : Vérifier si une vente peut générer une facture
  const canGenerateInvoice = (sale) => {
    if (!sale) {
      return { valid: false, message: "Données de vente manquantes" };
    }

    const hasCustomer = sale.customer?.name || sale.customerName;
    if (!hasCustomer) {
      return {
        valid: false,
        message:
          "Cette vente ne peut pas générer de facture (nom du client manquant)",
      };
    }

    const hasItems = (sale.items || sale.saleItems || []).length > 0;
    if (!hasItems) {
      return {
        valid: false,
        message:
          "Cette vente ne peut pas générer de facture (aucun article trouvé)",
      };
    }

    const hasValidTotal = sale.total && sale.total > 0;
    if (!hasValidTotal) {
      return {
        valid: false,
        message:
          "Cette vente ne peut pas générer de facture (montant invalide)",
      };
    }

    return { valid: true };
  };

  const loadExpenses = async () => {
    try {
      console.log("💰 Chargement dépenses du jour...");
      console.log("👤 Utilisateur:", {
        id: user?.id,
        role: user?.role,
        productAccessType: user?.productAccessType,
      });

      // ✅ Le backend filtre automatiquement les dépenses selon le rôle et productAccessType
      const todayExpenses = await expenseService.getExpensesByDate(new Date());

      setExpenses(todayExpenses);

      console.log(
        `✅ ${todayExpenses.length} dépenses chargées (filtrées par le backend)`
      );
      console.log("📋 Détail dépenses:", todayExpenses);
    } catch (error) {
      console.error("❌ Erreur lors du chargement des dépenses:", error);
      setExpenses([]);
    }
  };

  // Gestionnaire quand une dépense est ajoutée
  const handleExpenseAdded = (newExpense) => {
    console.log("➕ Nouvelle dépense ajoutée:", newExpense);

    // Ajouter la dépense à la liste
    setExpenses((prev) => [...prev, newExpense]);

    // Afficher un message de succès
    setSuccess("Dépense ajoutée avec succès au solde de caisse");
    setTimeout(() => setSuccess(null), 3000);

    // Recharger le solde de caisse
    loadCashBalance();
  };

  // Fonction modifiée pour calculer le solde de caisse avec dépenses
  /**
   * ✅ NOUVEAU : Calcul du solde caisse basé sur sale_payments
   */
  const calculateCashBalanceWithExpenses = async (expenses) => {
    try {
      console.log("💰 Calcul solde caisse...");
      console.log("👤 Utilisateur actuel:", {
        id: user?.id,
        username: user?.username,
        role: user?.role,
        productAccessType: user?.productAccessType,
      });

      // ✅ Récupérer les paiements en espèces (déjà filtrés par le backend)
      const cashBalance = await saleService.calculateCashBalanceFromPayments();

      if (!cashBalance.success) {
        console.warn(
          "⚠️ Impossible de récupérer le solde caisse depuis les paiements"
        );
        return {
          cashIncome: 0,
          totalExpenses: 0,
          netBalance: 0,
        };
      }

      // ✅ Calculer le total des dépenses (déjà filtrées par le backend)
      const totalExpenses = expenses.reduce(
        (sum, expense) => sum + (parseFloat(expense.amount) || 0),
        0
      );

      const cashIncome = cashBalance.cashAmount;
      const netBalance = cashIncome - totalExpenses;

      console.log("💰 Solde caisse calculé (filtré par backend):", {
        cashIncome,
        totalExpenses,
        netBalance,
        transactionCount: cashBalance.transactionCount,
        userRole: user?.role,
        userAccess: user?.productAccessType,
        filteredByBackend: true,
      });

      return {
        cashIncome,
        totalExpenses,
        netBalance,
        transactionCount: cashBalance.transactionCount,
        allPaymentMethods: cashBalance.allMethods,
      };
    } catch (error) {
      console.error("❌ Erreur calcul solde caisse:", error);
      return {
        cashIncome: 0,
        totalExpenses: 0,
        netBalance: 0,
      };
    }
  };

  const loadCashBalance = async () => {
    console.log("🔄 Rechargement solde de caisse...");
    // ✅ Recharger les dépenses fraîches du serveur (pas dépendre du state expenses)
    let currentExpenses = expenses;
    try {
      const freshExpenses = await expenseService.getExpensesByDate(new Date());
      currentExpenses = freshExpenses || [];
    } catch (e) {
      console.warn("⚠️ Impossible de recharger les dépenses pour le solde");
    }
    const balance = await calculateCashBalanceWithExpenses(currentExpenses);
    setCashBalanceData(balance);
    console.log("✅ Solde de caisse mis à jour:", balance);
  };

  // ✅ Chargement en 2 phases : ventes du jour d'abord, puis toutes les ventes en arrière-plan
  // ✅ NOUVEAU : Chargement via pagination serveur avec filtres
  const loadSales = async (page = 0) => {
    try {
      setLoading(true);
      setError(null);

      // ✅ Construire les filtres pour le serveur
      const filters = {
        dateFilter: customDateFilter ? 'all' : dateFilter,
        status: statusFilter || undefined,
        payment: paymentFilter || undefined,
        search: searchTerm.trim() || undefined,
        showCancelled: showCancelled,
      };

      // Si date personnalisée, envoyer les bornes
      if (customDateFilter && customDateFilter.dateMode) {
        if (customDateFilter.dateMode === 'single' && customDateFilter.selectedDate) {
          const d = new Date(customDateFilter.selectedDate);
          filters.startDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0).toISOString();
          filters.endDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).toISOString();
        } else if (customDateFilter.dateMode === 'range' && customDateFilter.startDate && customDateFilter.endDate) {
          const sDate = new Date(customDateFilter.startDate);
          const eDate = new Date(customDateFilter.endDate);
          filters.startDate = new Date(sDate.getFullYear(), sDate.getMonth(), sDate.getDate(), 0, 0, 0).toISOString();
          filters.endDate = new Date(eDate.getFullYear(), eDate.getMonth(), eDate.getDate(), 23, 59, 59).toISOString();
        }
      }

      console.log('📊 Chargement ventes filtrées:', { page, filters });

      // ✅ Charger ventes paginées + stats en PARALLÈLE
      const [salesResponse, statsResponse] = await Promise.all([
        saleService.getSalesFiltered(page, itemsPerPage, filters),
        saleService.getSalesStatsAggregated(
          filters.dateFilter || 'today',
          filters.startDate || null,
          filters.endDate || null
        ),
      ]);

      // ✅ Traiter les ventes
      if (salesResponse.success) {
        setSalesData(salesResponse.data);
        setFilteredSales(salesResponse.data);
        
        if (salesResponse.pagination) {
          setServerPagination(salesResponse.pagination);
        }
        
        // ✅ NOUVEAU : Stocker les stats filtrées (revenue + count exacts)
        setServerStats(prev => ({
          ...prev,
          periodStats: {
            salesCount: salesResponse.filteredCount ?? salesResponse.pagination?.totalElements ?? 0,
            revenue: salesResponse.filteredRevenue ?? 0,
          }
        }));
      } else {
        setError(salesResponse.message || 'Erreur lors du chargement');
        setSalesData([]);
        setFilteredSales([]);
      }

      // ✅ Traiter les stats globales (compteurs par période pour les dropdowns)
      if (statsResponse.success && statsResponse.data) {
        // Mettre à jour filterCounts pour les dropdowns (compteurs globaux)
        const fc = statsResponse.data.filterCounts || {};
        const pm = statsResponse.data.paymentMethods || {};
        
        setServerStats(prev => ({
          ...prev,
          filterCounts: fc,
          paymentMethods: pm,
        }));
        
        setFilterCounts({
          total: fc.total || 0,
          today: fc.today || 0,
          week: fc.week || 0,
          month: fc.month || 0,
          year: fc.year || 0,
          paid: fc.paid || 0,
          credit: fc.credit || 0,
          creditPaid: fc.creditPaid || 0,
          paymentMethods: pm,
        });
      }

    } catch (error) {
      console.error('❌ Erreur chargement ventes:', error);
      setError('Erreur de connexion');
      setSalesData([]);
      setFilteredSales([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Vérifier si une vente peut être annulée (moins de 24h)
   */
  const canCancelSale = (sale) => {
    if (!sale.createdAt) return false;

    const saleDate = new Date(sale.createdAt);
    const now = new Date();
    const hoursDiff = (now - saleDate) / (1000 * 60 * 60);

    return hoursDiff < 24 && !sale.isCancelled && isManager;
  };

  /**
   * Calculer les heures restantes pour annuler
   */
  const getHoursRemaining = (sale) => {
    if (!sale.createdAt) return 0;

    const saleDate = new Date(sale.createdAt);
    const now = new Date();
    const hoursDiff = (now - saleDate) / (1000 * 60 * 60);

    return Math.max(0, 24 - hoursDiff);
  };

  /**
   * Ouvrir le modal d'annulation
   */
  const handleCancelSale = (sale) => {
    console.log("🚫 Demande d'annulation de vente:", sale.saleNumber);
    setSaleToCancel(sale);
    setCancelReason("");
    setShowCancelModal(true);
  };

  /**
   * Confirmer l'annulation
   */
  const confirmCancelSale = async () => {
    if (!saleToCancel) return;

    if (!cancelReason.trim()) {
      setError("Veuillez indiquer la raison de l'annulation");
      return;
    }

    try {
      setCancelling(true);
      setError(null);

      console.log(
        "🚫 Annulation vente:",
        saleToCancel.id,
        "Raison:",
        cancelReason
      );

      const response = await saleService.cancelSale(
        saleToCancel.id,
        cancelReason
      );

      if (response.success) {
        setSuccess(`Vente ${saleToCancel.saleNumber} annulée avec succès`);

        // Fermer le modal
        setShowCancelModal(false);
        setSaleToCancel(null);
        setCancelReason("");

        // ✅ Recharger ventes + caisse après annulation
        // loadCashBalance() recharge les dépenses fraîches du serveur lui-même
        setTimeout(async () => {
          await loadSales(currentPage - 1);
          await loadCashBalance();
        }, 500);

        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(response.message || "Erreur lors de l'annulation");
      }
    } catch (error) {
      console.error("❌ Erreur annulation vente:", error);
      setError(error.message || "Erreur lors de l'annulation");
    } finally {
      setCancelling(false);
    }
  };

  // ✅ Les compteurs sont calculés côté serveur via getSalesStatsAggregated
  const calculateFilterCounts = () => {
    // No-op : les filtres sont maintenant calculés côté serveur
  };

  const getPeriodLabel = () => {
    const labels = {
      all: "toutes les dates",
      today: "aujourd'hui",
      week: "cette semaine",
      month: "ce mois",
      year: "cette année",
    };
    return labels[dateFilter] || "période sélectionnée";
  };

  // Fonction pour obtenir le label détaillé avec dates pour le dropdown
  const getDateFilterLabel = () => {
    if (customDateFilter) {
      if (customDateFilter.dateMode === "single") {
        return new Date(customDateFilter.selectedDate).toLocaleDateString("fr-FR");
      } else if (customDateFilter.dateMode === "range") {
        const start = new Date(customDateFilter.startDate).toLocaleDateString("fr-FR", { day: '2-digit', month: '2-digit' });
        const end = new Date(customDateFilter.endDate).toLocaleDateString("fr-FR", { day: '2-digit', month: '2-digit', year: 'numeric' });
        return `${start} - ${end}`;
      }
    }
    
    const labels = {
      all: "Toutes les dates",
      today: "Aujourd'hui",
      week: "Cette semaine",
      month: "Ce mois",
      year: "Cette année",
    };
    return labels[dateFilter] || "Toutes les dates";
  };

  // 3. Modifiez votre fonction getFilteredSales pour prendre en compte le filtre personnalisé
  // ✅ SIMPLIFIÉ : Les données sont déjà filtrées par le serveur
  const getFilteredSales = () => {
    return salesData; // Déjà filtré + paginé par le serveur
  };

  // Pagination : données déjà paginées par le serveur, on retourne les filtrées
  // Pagination locale sur les données filtrées
   // ✅ SIMPLIFIÉ : La pagination est déjà faite côté serveur
  const getPaginatedSales = () => {
    return salesData; // Déjà la bonne page
  };

  const handleExpenseDeleted = async () => {
    console.log("🗑️ Dépense supprimée, mise à jour...");

    try {
      // Recharger les dépenses
      const updatedExpenses = await expenseService.getExpensesByDate(
        new Date()
      );
      setExpenses(updatedExpenses);

      // Recalculer le solde avec les nouvelles dépenses
      const balance = await calculateCashBalanceWithExpenses(updatedExpenses);
      setCashBalanceData(balance);

      console.log("✅ Solde mis à jour:", balance);
      setSuccess("Solde de caisse mis à jour");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("❌ Erreur:", error);
      setError("Erreur lors de la mise à jour du solde");
    }
  };

   const getTotalPages = () => {
    return serverPagination.totalPages || 1;
  };

  // ✅ Navigation entre les pages
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= getTotalPages()) {
      setCurrentPage(newPage);
      // ✅ NOUVEAU : Charger la nouvelle page depuis le serveur
      loadSales(newPage - 1); // Le serveur est 0-indexed
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ✅ Changer le nombre d'éléments par page
   const handlePageSizeChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
    // Le useEffect se déclenche automatiquement via [itemsPerPage]
  };

  
  // ✅ NOUVEAU : Stats basées sur le serveur
  const isFilterActive = dateFilter !== "today" || 
    customDateFilter !== null || 
    paymentFilter !== "" || 
    statusFilter !== "" || 
    searchTerm !== "" ||
    showCancelled;
  
  // ✅ Compteur de ventes = totalElements de la pagination (toujours exact car filtré côté serveur)
  const displayedSalesCount = serverPagination.totalElements ?? 0;

  // ✅ Chiffre d'affaires = revenue filtré retourné par le serveur
  const displayedRevenue = serverStats.periodStats?.revenue ?? 0;
    
  // ✅ Caisse : garder le calcul existant via endpoint dédié
  const displayedCashAmount = isFilterActive 
    ? 0  // En mode filtré, la caisse n'a pas de sens (elle est toujours "aujourd'hui")
    : cashBalanceData.cashIncome;

  const handleCreateNewSale = () => {
    console.log("🆕 Redirection vers Point de Vente (POS)");
    navigate("/pos");
  };

  // Gestion des modals
  const handleViewSale = (sale) => {
    console.log("👁️ Affichage des détails de la vente:", sale);
    console.log("🔍 isMultiPayment:", sale.isMultiPayment);
    console.log("🔍 paymentDetails:", sale.paymentDetails);
    console.log("🔍 paymentMethod:", sale.paymentMethod);
    console.log("🔍 Données brutes:", JSON.stringify(sale, null, 2));

    setSelectedSale(sale);
    setShowDetailModal(true);
  };

  const handlePreviewSale = async (sale) => {
    try {
      // // ✅ CORRECTION: Vérifier et formater les items correctement
      let saleItems = [];

      if (sale.items && Array.isArray(sale.items)) {
        saleItems = sale.items;
      } else if (sale.saleItems && Array.isArray(sale.saleItems)) {
        saleItems = sale.saleItems;
      } else {
        console.warn("⚠️ Aucun item trouvé dans la vente:", sale);
        saleItems = []; // Array vide pour éviter l'erreur
      }

      console.log("🔍 Items pour aperçu:", saleItems);
      console.log("🔍 Remise pour aperçu:", sale.discount);

      const saleDataForPrint = {
        customer: {
          name: sale.customer?.name || sale.customerName || "Client anonyme",
          phone: sale.customer?.phone || sale.customerPhone || null,
        },
        items: saleItems.map((item) => ({
          id: item.id,
          productId: item.productId,
          name: item.product_name || item.name,
          productName: item.product_name || item.name,
          quantity: item.quantity || 0,
          price: item.unit_price || item.unitPrice || item.price || 0,
          unitPrice: item.unit_price || item.unitPrice || item.price || 0,
          totalPrice:
            item.totalPrice ||
            item.quantity *
              (item.unit_price || item.unitPrice || item.price || 0),
          productType: item.productType || "STOCKABLE",
          isService: item.productType === "SERVICE",
          typeIcon: item.productType === "SERVICE" ? "🔧" : "📦",
        })),
        paymentMethod: sale.paymentMethod,
        isCredit: sale.isCredit || sale.paymentMethod === "CREDIT",
        // ✅ CORRECTION: S'assurer que discount est bien passé
        discount: sale.discount || 0,
        notes: sale.notes || "",
        saleNumber: sale.saleNumber || sale.id,
        date: new Date(sale.createdAt || sale.saleDate),
        subtotal: sale.subtotal || sale.total,
        total: sale.total,
      };

      const boutiqueInfo = {
        name: currentBoutique?.name || "Ma Boutique",
        address: currentBoutique?.address || "Dakar, Sénégal",
        phone: currentBoutique?.phone || "+221 XX XXX XX XX",
        phone1: currentBoutique?.phone1 || "",
        email: currentBoutique?.email || null,
        // ✅ CORRECTION: Ajouter les données du logo
        logoUrl: currentBoutique?.logoUrl || currentBoutique?.logo_url || currentBoutique?.logo,
        logo: currentBoutique?.logo || currentBoutique?.logoUrl || currentBoutique?.logo_url,
        primaryColor: currentBoutique?.primaryColor || currentBoutique?.primary_color || '#3b82f6',
        secondaryColor: currentBoutique?.secondaryColor || currentBoutique?.secondary_color || '#1d4ed8'
      };

      const creatorInfo = getOriginalCreatorInfo(sale);

      const result = await TicketPrintService.previewTicket(
        saleDataForPrint,
        boutiqueInfo,
        creatorInfo
      );

      if (!result.success) {
        setError(result.message || "Erreur lors de l'aperçu");
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error("Erreur aperçu:", error);
      setError("Erreur lors de l'aperçu du ticket");
      setTimeout(() => setError(null), 5000);
    }
  };

  // 4. Gestionnaire pour le filtre personnalisé
  const handleCustomDateFilter = (filterData) => {
    setCustomDateFilter(filterData);
    
    // Si on réinitialise (filterData.dateMode === null), revenir à "today"
    if (filterData.dateMode === null) {
      setDateFilter("today");
    } else if (filterData.dateMode) {
      // Sinon, réinitialiser le filtre de date prédéfini si un filtre personnalisé est appliqué
      setDateFilter("all");
    }
  };

  const getOriginalCreatorInfo = (sale) => {
    // Utiliser les infos du vendeur de la vente
    if (
      sale.seller &&
      (sale.seller.name || sale.seller.firstName || sale.seller.lastName)
    ) {
      return {
        id: sale.seller.id,
        name:
          sale.seller.name ||
          `${sale.seller.firstName || ""} ${
            sale.seller.lastName || ""
          }`.trim() ||
          "Vendeur inconnu",
        firstName: sale.seller.firstName,
        lastName: sale.seller.lastName,
        role: sale.seller.role,
      };
    }

    // Fallback avec les champs seller_name, seller_id
    if (sale.sellerName || sale.seller_name) {
      return {
        id: sale.sellerId || sale.seller_id,
        name: sale.sellerName || sale.seller_name || "Vendeur inconnu",
        firstName: null,
        lastName: null,
        role: null,
      };
    }

    // Si pas d'info vendeur, utiliser "Employé" générique
    return {
      id: null,
      name: "Employé",
      firstName: null,
      lastName: null,
      role: null,
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      console.error("Date invalide:", dateString);
      return "";
    }

    return date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Africa/Dakar",
      hour12: false,
    });
  };

  const getPaymentMethodBadge = (method) => {
    const methods = {
      CASH: { icon: "💵", label: "Espèces" },
      WAVE: { icon: "📱", label: "Wave" },
      ORANGE_MONEY: { icon: "🍊", label: "Orange Money" },
      CREDIT: { icon: "📝", label: "Crédit" },
      CARD: { icon: "💳", label: "Carte" },
      TRANSFER: { icon: "🏦", label: "Virement" },
      BANK_TRANSFER: { icon: "🏦", label: "Virement" },
      CHECK: { icon: "📋", label: "Chèque" },
      MIXED: { icon: "🔄", label: "Paiement mixte" },
    };

    const methodInfo = methods[method];
    return methodInfo ? `${methodInfo.icon} ${methodInfo.label}` : method;
  };

  // Fonctions d'impression
  const handlePrintSale = async (sale) => {
    try {
      setPrinting(sale.id);

      console.log("🖨️ Préparation impression pour vente:", sale);

      // ✅ CORRECTION: Récupérer les articles avec fallback robuste
      let saleItems = [];
      if (sale.items && Array.isArray(sale.items)) {
        saleItems = sale.items;
      } else if (sale.saleItems && Array.isArray(sale.saleItems)) {
        saleItems = sale.saleItems;
      } else {
        console.warn(
          "⚠️ Aucun article trouvé dans la vente, récupération depuis le backend..."
        );
        // Optionnel: recharger la vente avec ses détails
        const saleDetails = await saleService.getSaleById(sale.id);
        if (saleDetails.success && saleDetails.data.items) {
          saleItems = saleDetails.data.items;
        }
      }

      console.log("📦 Articles pour impression:", saleItems);

      const saleDataForPrint = {
        customer: {
          name: sale.customerName || "Client anonyme",
          phone: sale.customerPhone || null,
        },
        items: saleItems.map((item) => ({
          id: item.id,
          productId: item.productId,
          name: item.product_name || item.name,
          productName: item.product_name || item.name,
          quantity: item.quantity || 0,
          price: item.unit_price || item.unitPrice || item.price || 0,
          unitPrice: item.unit_price || item.unitPrice || item.price || 0,
          totalPrice:
            item.totalPrice ||
            item.quantity *
              (item.unit_price || item.unitPrice || item.price || 0),
          productType: item.productType || "STOCKABLE",
          isService: item.productType === "SERVICE",
          typeIcon: item.productType === "SERVICE" ? "🔧" : "📦",
        })),
        paymentMethod: sale.paymentMethod,
        isCredit: sale.isCredit || sale.paymentMethod === "CREDIT",
        // ✅ CORRECTION: Ajouter discount au niveau racine
        discount: sale.discount || 0,
        totals: {
          subtotal: sale.subtotal || 0,
          discount: sale.discount || 0,
          total: sale.total || 0,
          formattedSubtotal: saleService.formatCurrency(sale.subtotal || 0),
          formattedDiscount: saleService.formatCurrency(sale.discount || 0),
          formattedTotal: saleService.formatCurrency(sale.total || 0),
        },
        saleNumber: sale.saleNumber,
        date: new Date(sale.createdAt || sale.saleDate),
        metadata: {
          saleDate: new Date(sale.createdAt).toLocaleString("fr-FR"),
          saleNumber: sale.saleNumber,
        },
        notes: sale.notes || null,
      };

      const boutiqueInfo = {
        name: currentBoutique?.name || "Ma Boutique",
        address: currentBoutique?.address || "Dakar, Sénégal",
        phone: currentBoutique?.phone || "+221 XX XXX XX XX",
        phone1: currentBoutique?.phone1 || "",
        email: currentBoutique?.email || null,
        // ✅ CORRECTION: Ajouter les données du logo
        logoUrl: currentBoutique?.logoUrl || currentBoutique?.logo_url || currentBoutique?.logo,
        logo: currentBoutique?.logo || currentBoutique?.logoUrl || currentBoutique?.logo_url,
        primaryColor: currentBoutique?.primaryColor || currentBoutique?.primary_color || '#3b82f6',
        secondaryColor: currentBoutique?.secondaryColor || currentBoutique?.secondary_color || '#1d4ed8'
      };
      const creatorInfo = getOriginalCreatorInfo(sale);

      console.log("🎫 Envoi à TicketPrintService:", saleDataForPrint);

      const result = await TicketPrintService.printTicket(
        saleDataForPrint,
        boutiqueInfo,
        creatorInfo
      );

      if (result.success) {
        setSuccess("Ticket imprimé avec succès !");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message || "Erreur lors de l'impression");
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error("❌ Erreur impression:", error);
      setError("Erreur lors de l'impression du ticket");
      setTimeout(() => setError(null), 5000);
    } finally {
      setPrinting(null);
    }
  };

  const canEditSales = () => {
    console.log("🔍 Vérification permissions édition:", {
      userRole: user?.role,
      canEdit: user?.role === "MANAGER" || user?.role === "EMPLOYEE",
      user: user,
    });
    return user?.role === "MANAGER" || user?.role === "EMPLOYEE";
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);

    // ✅ DEBOUNCE : Attendre 400ms avant de chercher côté serveur
    if (searchDebounce) clearTimeout(searchDebounce);
    setSearchDebounce(setTimeout(() => {
      loadSales(0);
    }, 400));
  };

  
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case "date":
        setDateFilter(value);
        break;
      case "payment":
        setPaymentFilter(value);
        break;
      case "status":
        setStatusFilter(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
    // ✅ NOUVEAU : Recharger depuis le serveur au prochain render
    // Le useEffect ci-dessous gère le rechargement
  };

  const getPaymentMethodLabel = (method) => {
    const methodInfo = paymentMethods.find((pm) => pm.value === method);
    const label = methodInfo ? methodInfo.label : method;
    const count = filterCounts.paymentMethods?.[method] || 0;
    return `${label} (${count})`;
  };

  // Interface JSX
  return (
    <>
      <Container fluid className="py-4">
        {/* Messages d'alerte */}
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError(null)}
            className="mb-4"
          >
            <AlertCircle size={20} className="me-2" />
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccess(null)}
            className="mb-4"
          >
            <Check size={20} className="me-2" />
            {success}
          </Alert>
        )}

        {/* Indicateur discret : chargement complet en arrière-plan */}
        {/* {loadingAll && (
          <div className="text-center mb-2">
            <small className="text-muted">
              <Spinner animation="border" size="sm" className="me-1" />
              Chargement des données complètes...
            </small>
          </div>
        )} */}

        {/* En-tête - RESPONSIVE */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div className="flex-grow-1">
                <h2 className="fw-bold mb-2 d-flex align-items-center">
                  <ShoppingCart size={28} className="me-2 text-primary" />
                  <span className="d-none d-sm-inline">Liste des Ventes</span>
                  <span className="d-sm-none">Ventes</span>
                </h2>
                <p className="text-muted mb-0 d-none d-md-block">
                  Gestion et suivi de toutes les ventes de votre boutique
                </p>
              </div>
              <Button
                variant="success"
                size="lg"
                className="w-md-auto"
                onClick={handleCreateNewSale}
              >
                <Plus size={20} className="me-2" />
                <span className="d-none d-sm-inline">Nouvelle Vente</span>
                <span className="d-sm-none">Nouvelle</span>
              </Button>
            </div>
          </Col>
        </Row>

        {/* Indicateur ventes du jour */}

        {/* Indicateur statistiques avec solde de caisse */}
        <Row className="mb-3">
          <Col>
            <div className="stats-compact-container">
              <Row className="g-2">
                {/* Nombre de ventes */}
                <Col md={3} sm={6}>
                  <div className="stat-card-compact">
                    <div className="stat-icon-compact">
                      <i className="bi bi-calendar-check"></i>
                    </div>
                    <div className="stat-info">
                      <span className="stat-number">{displayedSalesCount}</span>
                      <span className="stat-label">Nombre de Ventes</span>
                    </div>
                  </div>
                </Col>

                {/* Chiffre d'affaires */}
                <Col md={4} sm={6}>
                  <div className="stat-card-compact">
                    <div className="stat-icon-compact bg-primary">
                      <i className="bi bi-graph-up-arrow"></i>
                    </div>
                    <div className="stat-info">
                      <span className="stat-number">
                        {formatCurrency(displayedRevenue)}
                      </span>
                      <span className="stat-label">Chiffre d'affaires</span>
                    </div>
                  </div>
                </Col>

                {/* Solde de caisse */}
                <Col md={5}>
                  <div className="cash-balance-compact">
                    <div className="cash-header-compact">
                      <div className="cash-title-compact">
                        <i className="bi bi-wallet2"></i>
                        <span className="stat-number">Solde caisse</span>
                      </div>
                      {!isFilterActive && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setShowExpenseModal(true)}
                        >
                          <Minus size={14} />
                          Dépense
                        </Button>
                      )}
                    </div>

                    <div className="cash-amounts-compact">
                      <div className="cash-item">
                        <span className="text-success">
                          +{formatCurrency(isFilterActive ? displayedCashAmount : cashBalanceData.cashIncome)}
                        </span>
                        {!isFilterActive && cashBalanceData.transactionCount && (
                          <small className="text-muted ms-1">
                            ({cashBalanceData.transactionCount} transactions)
                          </small>
                        )}
                      </div>
                      {!isFilterActive && (
                        <>
                          <div className="cash-separator">-</div>
                          <div className="cash-item">
                            <span className="text-danger">
                              {formatCurrency(cashBalanceData.totalExpenses)}
                            </span>
                          </div>
                          <div className="cash-separator">=</div>
                          <div className="cash-item">
                            <span
                              className={`fw-bold ${
                                cashBalanceData.netBalance >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }`}
                            >
                              {formatCurrency(cashBalanceData.netBalance)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

              <style jsx>{`
                .stats-compact-container {
                  padding: 0.5rem 0;
                }

                .stat-card-compact {
                  background: white;
                  border-radius: 6px;
                  padding: 0.75rem;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                  border: 1px solid #e9ecef;
                  display: flex;
                  align-items: center;
                  gap: 0.75rem;
                  height: 60px;
                  transition: transform 0.2s ease;
                }

                .stat-card-compact:hover {
                  transform: translateY(-1px);
                  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
                }

                .stat-icon-compact {
                  width: 32px;
                  height: 32px;
                  border-radius: 6px;
                  background: #28a745;
                  color: white;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 0.9rem;
                  flex-shrink: 0;
                }

                .stat-icon-compact.bg-primary {
                  background: #007bff;
                }

                .stat-info {
                  display: flex;
                  flex-direction: column;
                  gap: 0.125rem;
                }

                .stat-number {
                  font-size: 1.1rem;
                  font-weight: 700;
                  color: #2c3e50;
                  line-height: 1;
                }

                .stat-label {
                  font-size: 0.75rem;
                  color: #6c757d;
                  font-weight: 500;
                }

                .cash-balance-compact {
                  background: white;
                  border-radius: 6px;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                  border: 1px solid #e9ecef;
                  height: 70px;
                  overflow: hidden;
                  position: relative;
                }

                .cash-content-wrapper {
                  padding: 0.75rem;
                  height: 100%;
                  display: flex;
                  flex-direction: column;
                  gap: 0.5rem;
                }

                .cash-header-compact {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                }

                .cash-title-compact {
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                  font-size: 0.9rem;
                  font-weight: 600;
                  color: #495057;
                }

                .cash-amounts-compact {
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                  font-size: 0.875rem;
                  flex: 1;
                  font-weight: 600;
                }

                .cash-item {
                  display: flex;
                  align-items: center;
                }

                .cash-separator {
                  color: #6c757d;
                  font-weight: 600;
                }

                @media (max-width: 768px) {
                  .stat-card-compact {
                    height: 50px;
                    padding: 0.5rem;
                    gap: 0.5rem;
                  }

                  .stat-icon-compact {
                    width: 28px;
                    height: 28px;
                    font-size: 0.8rem;
                  }

                  .stat-number {
                    font-size: 0.95rem;
                  }

                  .stat-label {
                    font-size: 0.7rem;
                  }

                  .cash-balance-compact {
                    height: 65px;
                  }

                  .cash-content-wrapper {
                    padding: 0.5rem;
                  }

                  .cash-title-compact {
                    font-size: 0.85rem;
                  }

                  .cash-amounts-compact {
                    font-size: 0.8rem;
                    gap: 0.4rem;
                    font-weight: 600;
                  }
                }

                @media (max-width: 576px) {
                  .stats-compact-container {
                    padding: 0.25rem 0;
                  }

                  .stat-card-compact {
                    height: 45px;
                    padding: 0.375rem;
                  }

                  .cash-balance-compact {
                    height: 60px;
                  }

                  .cash-content-wrapper {
                    padding: 0.4rem;
                  }

                  .stat-icon-compact {
                    width: 24px;
                    height: 24px;
                    font-size: 0.75rem;
                  }

                  .stat-number {
                    font-size: 0.875rem;
                  }

                  .cash-amounts-compact {
                    font-size: 0.75rem;
                    gap: 0.3rem;
                    font-weight: 600;
                    flex-wrap: wrap;
                  }

                  .cash-title-compact {
                    font-size: 0.8rem;
                  }

                  .cash-item {
                    white-space: nowrap;
                  }

                  .cash-separator {
                    font-size: 0.8rem;
                  }
                }
              `}</style>
          </Col>
        </Row>
        
        {/* Barre de recherche et filtres - RESPONSIVE */}
        <Row className="mb-4">
          <Col xs={12} md={3} className="mb-3 mb-md-0">
            <InputGroup>
              <InputGroup.Text>
                <Search size={16} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Rechercher par N°, client, vendeur..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </InputGroup>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col xs={12} md={12}>
            <div className="d-flex gap-2 flex-wrap justify-content-md-start justify-content-center">
              {/* Filtre par date */}
              <Dropdown>
                <Dropdown.Toggle
                  variant="outline-primary"
                  size="sm"
                  className="d-flex align-items-center"
                >
                  <Calendar size={16} className="me-1" />
                  <span className="d-none d-sm-inline">
                    {getDateFilterLabel()}
                  </span>
                  <span className="d-sm-none">
                    📅 {filterCounts[dateFilter] || filterCounts.total || 0}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      handleFilterChange("date", "today");
                      setCustomDateFilter(null);
                    }}
                    active={dateFilter === "today" && !customDateFilter}
                  >
                    Aujourd'hui ({filterCounts.today || 0})
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      handleFilterChange("date", "week");
                      setCustomDateFilter(null);
                    }}
                    active={dateFilter === "week" && !customDateFilter}
                  >
                    Cette semaine ({filterCounts.week || 0})
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      handleFilterChange("date", "month");
                      setCustomDateFilter(null);
                    }}
                    active={dateFilter === "month" && !customDateFilter}
                  >
                    Ce mois ({filterCounts.month || 0})
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      handleFilterChange("date", "all");
                      setCustomDateFilter(null);
                    }}
                    active={dateFilter === "all" && !customDateFilter}
                  >
                    Toutes les dates ({filterCounts.total || 0})
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {/* Filtre par mode de paiement */}
              <Dropdown>
                <Dropdown.Toggle
                  variant="outline-success"
                  size="sm"
                  className="d-flex align-items-center"
                >
                  <CreditCard size={16} className="me-1" />
                  <span className="d-none d-sm-inline">
                    {!paymentFilter && `Tous paiements`}
                    {paymentFilter && getPaymentMethodLabel(paymentFilter)}
                  </span>
                  <span className="d-sm-none">
                    💳{" "}
                    {paymentFilter
                      ? filterCounts.paymentMethods?.[paymentFilter] || 0
                      : "Tous"}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => handleFilterChange("payment", "")}
                    active={!paymentFilter}
                  >
                    <span className="me-2">💳</span>
                    Tous les paiements {getPeriodLabel()} (
                    {Object.values(filterCounts.paymentMethods || {}).reduce(
                      (a, b) => a + b,
                      0
                    )}
                    )
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  {paymentMethods
                    .filter((method) => {
                      // ✅ SIMPLE : Filtrer le crédit si la boutique ne peut pas l'utiliser
                      if (method.value === "CREDIT") {
                        return canShowCredits(currentBoutique);
                      }
                      return true; // Garder tous les autres modes de paiement
                    })
                    .map((method) => (
                      <Dropdown.Item
                        key={method.value}
                        onClick={() =>
                          handleFilterChange("payment", method.value)
                        }
                        active={paymentFilter === method.value}
                      >
                        <span className="me-2">{method.icon}</span>
                        {getPaymentMethodLabel(method.value)}
                      </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
              </Dropdown>

              {/* Filtre par statut */}
              <Dropdown>
                <Dropdown.Toggle
                  variant="outline-info"
                  size="sm"
                  className="d-flex align-items-center"
                >
                  <DollarSign size={16} className="me-1" />
                  <span className="d-none d-sm-inline">
                    {!statusFilter && `Tous statuts`}
                    {statusFilter === "paid" &&
                      `Payé (${filterCounts.paid || 0})`}
                    {statusFilter === "credit" &&
                      `Crédit (${filterCounts.credit || 0})`}
                    {statusFilter === "creditPaid" &&
                      `Crédit soldé (${filterCounts.creditPaid || 0})`}
                  </span>
                  <span className="d-sm-none">
                    💰 {statusFilter ? filterCounts[statusFilter] || 0 : "Tous"}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => handleFilterChange("status", "")}
                    active={!statusFilter}
                  >
                    <span className="me-2">💰</span>
                    Tous les statuts {getPeriodLabel()} (
                    {(filterCounts.paid || 0) +
                      (filterCounts.credit || 0) +
                      (filterCounts.creditPaid || 0)}
                    )
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={() => handleFilterChange("status", "paid")}
                    active={statusFilter === "paid"}
                  >
                    <span className="me-2">✅</span>
                    Payé ({filterCounts.paid || 0})
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleFilterChange("status", "credit")}
                    active={statusFilter === "credit"}
                  >
                    <span className="me-2">⏳</span>
                    Crédit en cours ({filterCounts.credit || 0})
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleFilterChange("status", "creditPaid")}
                    active={statusFilter === "creditPaid"}
                  >
                    <span className="me-2">💚</span>
                    Crédit soldé ({filterCounts.creditPaid || 0})
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {/* Bouton actualiser */}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  loadSales();
                  loadExpenses();
                }}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <Download size={16} className="me-1 d-none d-sm-inline" />
                    <Download size={16} className="d-sm-none" />
                    <span className="d-none d-sm-inline">Actualiser</span>
                  </>
                )}
              </Button>

              {/* ✅ Filtre de date personnalisé */}
                 
               <CustomDateFilter onFilterChange={handleCustomDateFilter} />


              {/* ✅ Checkbox pour afficher les ventes annulées */}
              {isManager && (
                <Form.Check
                  type="checkbox"
                  id="show-cancelled-checkbox"
                  label={
                    <span className="d-flex align-items-center">
                      <X size={16} className="me-1 text-danger" />
                      <span className="d-none d-md-inline">Annulées</span>
                    </span>
                  }
                  checked={showCancelled}
                  onChange={(e) => setShowCancelled(e.target.checked)}
                  className="ms-2 d-flex align-items-center"
                  style={{ fontSize: '0.875rem' }}
                />
              )}
            </div>
          </Col>
        </Row>

        {/* Liste des ventes */}
        <Row>
          <Col>
            <Card>
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Chargement des ventes...</p>
                  </div>
                ) : getPaginatedSales().length === 0 ? (
                  <div className="text-center py-5">
                    <ShoppingCart size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">Aucune vente trouvée</h5>
                    <p className="text-muted mb-4 px-3">
                      {serverPagination.totalElements === 0
                        ? "Commencez par créer votre première vente"
                        : "Aucune vente ne correspond à vos critères de recherche"}
                    </p>
                    <Button
                      variant="success"
                      onClick={handleCreateNewSale}
                      className="px-4"
                    >
                      <Plus size={16} className="me-2" />
                      <span className="d-none d-sm-inline">Nouvelle Vente</span>
                      <span className="d-sm-none">Nouvelle</span>
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* ✅ TABLE DESKTOP CORRIGÉE */}
                    <div className="d-none d-lg-block">
                      <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>N° Vente</th>
                            <th>Client</th>
                            <th>Vendeur</th>
                            <th>Articles</th>
                            <th>Total</th>
                            <th>Paiement</th>
                            <th>Statut</th>
                            <th>Date</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPaginatedSales().map((sale) => (
                            <tr key={sale.id} className="align-middle">
                              <td>
                                <code className="bg-light px-2 py-1 rounded">
                                  {sale.saleNumber}
                                </code>
                              </td>

                              <td>
                                <div>
                                  <div className="fw-semibold">
                                    {sale.customer?.name || "Client anonyme"}
                                  </div>
                                  {sale.customer?.phone && (
                                    <small className="text-muted">
                                      <Phone size={12} className="me-1" />
                                      {sale.customer.phone}
                                    </small>
                                  )}
                                </div>
                              </td>

                              <td>
                                <div className="d-flex align-items-center">
                                  <User size={16} className="me-2 text-muted" />
                                  <span>{sale.sellerName || "Vendeur"}</span>
                                </div>
                              </td>

                              <td>
                                <Badge bg="secondary" className="px-3 py-2">
                                  {sale.totalQuantity || 0} article(s)
                                </Badge>
                              </td>

                              <td>
                                <div className="fw-bold text-success fs-6">
                                  {formatCurrency(sale.total || 0)}
                                </div>
                              </td>

                              <td>
                                <Badge bg="info" className="px-3 py-2">
                                  {getPaymentMethodBadge(sale.paymentMethod)}
                                </Badge>
                              </td>

                              <td>
                                {sale.isCancelled ? (
                                  <Badge bg="danger" className="px-3 py-2">
                                    <X size={14} className="me-1" />
                                    Annulée
                                  </Badge>
                                ) : (
                                  <Badge
                                    bg={
                                      sale.isCredit
                                        ? sale.isCreditPaid
                                          ? "success"
                                          : "warning"
                                        : "success"
                                    }
                                    className="px-3 py-2"
                                  >
                                    {sale.isCredit
                                      ? sale.isCreditPaid
                                        ? "Crédit soldé"
                                        : "En attente"
                                      : "Payé"}
                                  </Badge>
                                )}
                              </td>

                              <td>
                                <div className="small">
                                  {formatDate(sale.createdAt || sale.saleDate)}
                                </div>
                              </td>

                              <td>
                                <div className="d-flex gap-1 justify-content-center">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleViewSale(sale)}
                                    title="Voir les détails"
                                  >
                                    <Eye size={16} />
                                  </Button>

                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handlePrintSale(sale)}
                                    disabled={printing === sale.id}
                                    title="Imprimer le ticket"
                                  >
                                    {printing === sale.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <Printer size={16} />
                                    )}
                                  </Button>
                                  {/* ✅ Bouton Annuler : seulement si pas déjà annulée et dans les 24h */}
                                  {canCancelSale(sale) && (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleCancelSale(sale)}
                                      title={`Peut être annulée pendant ${Math.round(
                                        getHoursRemaining(sale)
                                      )}h encore`}
                                    >
                                      <X size={16} className="me-1" />
                                    </Button>
                                  )}

                                  {/* ✅ BOUTON FACTURE DESKTOP */}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {/* Cards mobile et tablette */}
                    <div className="d-lg-none">
                      {getPaginatedSales().map((sale) => (
                        <Card key={sale.id} className="mb-3 shadow-sm">
                          <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <code className="bg-light px-2 py-1 rounded fs-6 fw-bold">
                                  {sale.saleNumber}
                                </code>
                                <div className="mt-1">
                                  {sale.isCancelled ? (
                                    <Badge bg="danger" className="me-2">
                                      <X size={14} className="me-1" />
                                      Annulée
                                    </Badge>
                                  ) : (
                                    <>
                                      <Badge
                                        bg={
                                          sale.isCredit
                                            ? sale.isCreditPaid
                                              ? "success"
                                              : "warning"
                                            : "success"
                                        }
                                        className="me-2"
                                      >
                                        {sale.isCredit
                                          ? sale.isCreditPaid
                                            ? "Crédit soldé"
                                            : "En attente"
                                          : "Payé"}
                                      </Badge>
                                      <Badge bg="info">
                                        {getPaymentMethodBadge(
                                          sale.paymentMethod
                                        )}
                                      </Badge>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="text-end">
                                <div className="fw-bold text-success fs-5 mb-1">
                                  {formatCurrency(sale.total || 0)}
                                </div>
                                <Badge bg="secondary">
                                  {sale.totalQuantity || 0} article(s)
                                </Badge>
                              </div>
                            </div>

                            <Row className="mb-3">
                              <Col xs={6}>
                                <div className="small text-muted mb-1">
                                  Client
                                </div>
                                <div className="fw-semibold">
                                  {sale.customer?.name || "Client anonyme"}
                                </div>
                                {sale.customer?.phone && (
                                  <div className="small text-muted">
                                    <Phone size={12} className="me-1" />
                                    {sale.customer.phone}
                                  </div>
                                )}
                              </Col>
                              <Col xs={6}>
                                <div className="small text-muted mb-1">
                                  Vendeur
                                </div>
                                <div className="d-flex align-items-center">
                                  <User size={14} className="me-1 text-muted" />
                                  <span>{sale.sellerName || "Vendeur"}</span>
                                </div>
                              </Col>
                            </Row>

                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <div className="small text-muted mb-1">
                                  Date
                                </div>
                                <div className="small">
                                  {formatDate(sale.createdAt || sale.saleDate)}
                                </div>
                              </div>

                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleViewSale(sale)}
                                  title="Voir les détails"
                                >
                                  <Eye size={16} />
                                </Button>

                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handlePrintSale(sale)}
                                  disabled={printing === sale.id}
                                  title="Imprimer le ticket"
                                >
                                  {printing === sale.id ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <Printer size={16} />
                                  )}
                                </Button>

                                {canCancelSale(sale) && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleCancelSale(sale)}
                                    title={`Peut être annulée pendant ${Math.round(
                                      getHoursRemaining(sale)
                                    )}h encore`}
                                  >
                                    <X size={16} className="me-1" />
                                  </Button>
                                )}

                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>

                    {/* Pagination */}
                    {getTotalPages() > 1 && (
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top gap-3">
                        <div className="d-flex align-items-center gap-2 order-2 order-md-1">
                          <small className="text-muted">
                            Affichage de {(currentPage - 1) * itemsPerPage + 1} à{" "}
                            {Math.min(currentPage * itemsPerPage, serverPagination.totalElements)}{" "}
                            sur {serverPagination.totalElements} ventes
                          </small>
                          <span className="text-muted">|</span>
                          <div className="d-flex align-items-center gap-1">
                            <Form.Select
                              size="sm"
                              value={itemsPerPage}
                              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                              style={{ width: 'auto', minWidth: '60px' }}
                            >
                              {pageSizeOptions.map(size => (
                                <option key={size} value={size}>{size}</option>
                              ))}
                            </Form.Select>
                            <small className="text-muted d-none d-sm-inline">/ page</small>
                          </div>
                        </div>

                        <div className="order-1 order-md-2">
                          {/* Mobile */}
                          <div className="d-md-none">
                            <div className="d-flex align-items-center gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                              >
                                ‹ Préc
                              </Button>
                              <span className="px-3 py-1 bg-light rounded">
                                {currentPage} / {getTotalPages()}
                              </span>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={currentPage === getTotalPages()}
                                onClick={() => handlePageChange(currentPage + 1)}
                              >
                                Suiv ›
                              </Button>
                            </div>
                          </div>

                          {/* Desktop — max 5 pages visibles + ellipsis */}
                          <Pagination className="mb-0 d-none d-md-flex">
                            <Pagination.Prev
                              disabled={currentPage === 1}
                              onClick={() => handlePageChange(currentPage - 1)}
                            />

                            {(() => {
                              const total = getTotalPages();
                              const pages = [];

                              if (total <= 5) {
                                // ≤ 5 pages : afficher tout (1 2 3 4 5)
                                for (let i = 1; i <= total; i++) {
                                  pages.push(
                                    <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
                                      {i}
                                    </Pagination.Item>
                                  );
                                }
                              } else {
                                // > 5 pages : max 5 niveaux visibles
                                // Début : 1 2 3 ... N
                                // Milieu : 1 ... 4 5 6 ... N
                                // Fin :    1 ... N-2 N-1 N

                                // Toujours page 1
                                pages.push(
                                  <Pagination.Item key={1} active={1 === currentPage} onClick={() => handlePageChange(1)}>
                                    1
                                  </Pagination.Item>
                                );

                                if (currentPage <= 3) {
                                  // Début : 1 [2 3] ... N
                                  for (let i = 2; i <= 3; i++) {
                                    pages.push(
                                      <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
                                        {i}
                                      </Pagination.Item>
                                    );
                                  }
                                  pages.push(<Pagination.Ellipsis key="end-e" disabled />);
                                } else if (currentPage >= total - 2) {
                                  // Fin : 1 ... [N-2 N-1] N
                                  pages.push(<Pagination.Ellipsis key="start-e" disabled />);
                                  for (let i = total - 2; i <= total - 1; i++) {
                                    pages.push(
                                      <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
                                        {i}
                                      </Pagination.Item>
                                    );
                                  }
                                } else {
                                  // Milieu : 1 ... [X] ... N
                                  pages.push(<Pagination.Ellipsis key="start-e" disabled />);
                                  // Page courante + voisines (max 3 au milieu)
                                  for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                                    if (i > 1 && i < total) {
                                      pages.push(
                                        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
                                          {i}
                                        </Pagination.Item>
                                      );
                                    }
                                  }
                                  pages.push(<Pagination.Ellipsis key="end-e" disabled />);
                                }

                                // Toujours dernière page
                                pages.push(
                                  <Pagination.Item key={total} active={total === currentPage} onClick={() => handlePageChange(total)}>
                                    {total}
                                  </Pagination.Item>
                                );
                              }

                              return pages;
                            })()}

                            <Pagination.Next
                              disabled={currentPage === getTotalPages()}
                              onClick={() => handlePageChange(currentPage + 1)}
                            />
                          </Pagination>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal de gestion des dépenses */}
      <ExpenseManager
        show={showExpenseModal}
        onHide={() => setShowExpenseModal(false)}
        onExpenseAdded={handleExpenseAdded}
        onExpenseDeleted={handleExpenseDeleted} // ✅ AJOUT
        currentDate={new Date()}
      />

      {/* Modal de détails de la vente */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <span className="me-2">📋</span>
            <span className="d-none d-sm-inline">
              Détails de la vente {selectedSale?.saleNumber}
            </span>
            <span className="d-sm-none">Vente {selectedSale?.saleNumber}</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3 p-md-4">
          {selectedSale && (
            <div>
              {/* ========== INFORMATIONS CLIENT ========== */}
              <div className="border rounded-3 p-3 mb-4 bg-light">
                <h6 className="d-flex align-items-center mb-3">
                  <User size={18} className="me-2 text-primary" />
                  Informations client
                </h6>
                <Row className="g-2 g-md-3">
                  <Col xs={12} md={6}>
                    <p className="mb-2">
                      <strong>Nom :</strong>{" "}
                      {selectedSale.customer?.name || "Client anonyme"}
                    </p>
                    <p className="mb-2 mb-md-0">
                      <strong>Téléphone :</strong>{" "}
                      {selectedSale.customer?.phone || "Non renseigné"}
                    </p>
                  </Col>
                  <Col xs={12} md={6}>
                    <p className="mb-2">
                      <strong>Vendeur :</strong>{" "}
                      {selectedSale.sellerName || "Vendeur"}
                    </p>
                    <p className="mb-0">
                      <strong>Date :</strong>{" "}
                      {formatDate(
                        selectedSale.createdAt || selectedSale.saleDate
                      )}
                    </p>
                  </Col>
                </Row>
              </div>

              {/* ✅ ALERTE VENTE ANNULÉE */}
              {selectedSale.isCancelled && (
                <Alert variant="danger" className="mb-4">
                  <div className="d-flex align-items-start">
                    <AlertCircle size={24} className="me-3 mt-1" />
                    <div className="flex-grow-1">
                      <Alert.Heading className="h6 mb-2">
                        <X size={18} className="me-2" />
                        Vente Annulée
                      </Alert.Heading>
                      {selectedSale.cancelledAt && (
                        <p className="mb-1">
                          <strong>Date d'annulation :</strong>{" "}
                          {formatDate(selectedSale.cancelledAt)}
                        </p>
                      )}
                      {selectedSale.cancellationReason && (
                        <p className="mb-1">
                          <strong>Objet annulation :</strong>{" "}
                          {selectedSale.cancellationReason}
                        </p>
                      )}
                      {selectedSale.cancelledBy && (
                        <p className="mb-0">
                          <strong>Annulée par :</strong>{" "}
                          {selectedSale.cancelledBy}
                        </p>
                      )}
                    </div>
                  </div>
                </Alert>
              )}

              {/* ========== INFORMATIONS DE PAIEMENT ========== */}
              {selectedSale && !selectedSale.isCancelled && (
                <div className="border rounded-3 p-3 mb-4 bg-light">
                  <h6 className="d-flex align-items-center mb-3">
                    <CreditCard size={18} className="me-2 text-success" />
                    Informations de paiement
                    {/* DEBUG - Retirez après test */}
                  </h6>

                  {/* Badges de statut */}
                  <div className="mb-3">
                    {selectedSale.isCancelled ? (
                      <Badge bg="danger" className="me-2">
                        <X size={16} className="me-1" />
                        Annulée
                      </Badge>
                    ) : (
                      <Badge
                        bg={
                          selectedSale.isCredit
                            ? selectedSale.isCreditPaid
                              ? "success"
                              : "warning"
                            : "success"
                        }
                        className="me-2"
                      >
                        {selectedSale.isCredit
                          ? selectedSale.isCreditPaid
                            ? "✅ Crédit soldé"
                            : "⏳ Crédit en cours"
                          : "✅ Payé"}
                      </Badge>
                    )}

                    {(selectedSale.isMultiPayment ||
                      selectedSale.paymentMethod === "MIXED") && (
                      <Badge bg="info">🔄 Paiement mixte</Badge>
                    )}
                  </div>

                  {/* ✅ VÉRIFICATION MULTIPLE des conditions */}
                  {(() => {
                    // Vérifier toutes les variantes possibles
                    const hasMultiPayment =
                      selectedSale.isMultiPayment ||
                      selectedSale.is_multi_payment ||
                      selectedSale.paymentMethod === "MIXED";

                    const paymentDetailsArray =
                      selectedSale.paymentDetails ||
                      selectedSale.payment_details ||
                      [];

                    const hasPaymentDetails =
                      Array.isArray(paymentDetailsArray) &&
                      paymentDetailsArray.length > 0;

                    console.log("🔍 Vérification paiement:", {
                      hasMultiPayment,
                      hasPaymentDetails,
                      paymentDetailsCount: paymentDetailsArray.length,
                      paymentMethod: selectedSale.paymentMethod,
                    });

                    // Si c'est un paiement multiple ET qu'on a les détails
                    if (hasMultiPayment && hasPaymentDetails) {
                      return (
                        <>
                          {/* PAIEMENT MULTIPLE */}
                          <p className="small text-muted mb-2 fw-semibold">
                            Répartition des paiements :
                          </p>
                          <div className="payment-details-list">
                            {paymentDetailsArray.map((payment, idx) => {
                              const paymentMethod =
                                payment.paymentMethod ||
                                payment.payment_method ||
                                payment.method ||
                                "CASH";

                              const methodColor =
                                saleService.getPaymentMethodColor(
                                  paymentMethod
                                );
                              const methodIcon =
                                payment.methodIcon ||
                                payment.payment_method_icon ||
                                saleService.getPaymentMethodIcon(paymentMethod);

                              const methodLabel =
                                payment.methodLabel ||
                                payment.payment_method_label ||
                                saleService.getPaymentMethodLabel(
                                  paymentMethod
                                );

                              const amount = payment.amount || 0;

                              const reference =
                                payment.reference ||
                                payment.referenceNumber ||
                                payment.reference_number ||
                                null;

                              return (
                                <div
                                  key={payment.id || idx}
                                  className="d-flex justify-content-between align-items-center py-3 px-3 mb-2 bg-white rounded border-start border-start-4"
                                  style={{
                                    borderLeftColor: `var(--bs-${methodColor})`,
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <div className="d-flex align-items-center flex-grow-1">
                                    <span
                                      className="me-3"
                                      style={{ fontSize: "1.5rem" }}
                                    >
                                      {methodIcon}
                                    </span>
                                    <div>
                                      <div
                                        className="fw-semibold"
                                        style={{ fontSize: "1rem" }}
                                      >
                                        {methodLabel}
                                      </div>
                                      {reference && (
                                        <small className="text-muted">
                                          Réf: {reference}
                                        </small>
                                      )}
                                      {payment.notes && (
                                        <small
                                          className="text-muted d-block"
                                          style={{ fontSize: "0.75rem" }}
                                        >
                                          📝 {payment.notes}
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-end ms-3">
                                    <div
                                      className="fw-bold text-success"
                                      style={{ fontSize: "1.15rem" }}
                                    >
                                      {formatCurrency(amount)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Total des paiements */}
                          <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                            <strong className="text-muted">Total payé :</strong>
                            <strong
                              className="text-success"
                              style={{ fontSize: "1.35rem" }}
                            >
                              {formatCurrency(
                                paymentDetailsArray.reduce(
                                  (sum, p) => sum + (parseFloat(p.amount) || 0),
                                  0
                                )
                              )}
                            </strong>
                          </div>
                        </>
                      );
                    } else {
                      // PAIEMENT SIMPLE
                      return (
                        <>
                          <div className="d-flex justify-content-between align-items-center py-2 px-3 bg-white rounded border">
                            <div className="d-flex align-items-center">
                              <span
                                className="me-2"
                                style={{ fontSize: "1.3rem" }}
                              >
                                {saleService.getPaymentMethodIcon(
                                  selectedSale.paymentMethod
                                )}
                              </span>
                              <strong>Méthode de paiement :</strong>
                            </div>
                            <Badge
                              bg={saleService.getPaymentMethodColor(
                                selectedSale.paymentMethod
                              )}
                              className="fs-6"
                            >
                              {selectedSale.paymentBadge ||
                                saleService.getPaymentMethodLabel(
                                  selectedSale.paymentMethod
                                )}
                            </Badge>
                          </div>

                          {/* Afficher un message si c'est MIXED mais sans détails */}
                          {selectedSale.paymentMethod === "MIXED" &&
                            !hasPaymentDetails && (
                              <Alert variant="info" className="mt-3 mb-0">
                                <small>
                                  ℹ️ Cette vente a été payée avec plusieurs
                                  méthodes, mais les détails ne sont pas
                                  disponibles.
                                </small>
                              </Alert>
                            )}

                          {/* Détails du crédit */}
                          {selectedSale.isCredit &&
                            !selectedSale.isCreditPaid && (
                              <div className="mt-3 p-3 bg-warning bg-opacity-10 rounded border border-warning">
                                <div className="d-flex justify-content-between mb-2">
                                  <span className="text-muted">
                                    Montant total :
                                  </span>
                                  <strong>
                                    {formatCurrency(selectedSale.total)}
                                  </strong>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                  <span className="text-muted">
                                    Déjà payé :
                                  </span>
                                  <span className="text-success fw-semibold">
                                    {formatCurrency(
                                      selectedSale.creditPaid || 0
                                    )}
                                  </span>
                                </div>
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between">
                                  <strong className="text-warning">
                                    Reste à payer :
                                  </strong>
                                  <strong
                                    className="text-warning"
                                    style={{ fontSize: "1.2rem" }}
                                  >
                                    {formatCurrency(
                                      selectedSale.creditBalance ||
                                        selectedSale.total -
                                          (selectedSale.creditPaid || 0)
                                    )}
                                  </strong>
                                </div>
                              </div>
                            )}
                        </>
                      );
                    }
                  })()}
                </div>
              )}
              {/* ========== SECTION 3 : ARTICLES VENDUS ========== */}
              <div className="border rounded-3 p-3 mb-4">
                <h6 className="d-flex align-items-center mb-3">
                  <Package size={18} className="me-2 text-info" />
                  Articles vendus ({selectedSale.items?.length || 0})
                </h6>

                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Article</th>
                        <th className="text-center">Qté</th>
                        <th className="text-end">P.U.</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedSale.items || []).map((item, index) => (
                        <tr key={index}>
                          <td>
                            <div className="fw-medium">
                              {item.product_name || item.name || "Produit"}
                            </div>
                            {item.categoryName && (
                              <small className="text-muted">
                                {item.categoryName}
                              </small>
                            )}
                          </td>
                          <td className="text-center">
                            <Badge bg="secondary">{item.quantity}</Badge>
                          </td>
                          <td className="text-end">
                            {formatCurrency(
                              item.unit_price ||
                                item.price ||
                                item.unitPrice ||
                                0
                            )}
                          </td>
                          <td className="text-end fw-semibold">
                            {formatCurrency(
                              (item.quantity || 0) *
                                (item.unit_price ||
                                  item.price ||
                                  item.unitPrice ||
                                  0)
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* ========== SECTION 4 : RÉSUMÉ FINANCIER ========== */}
              <div className="border rounded-3 p-3 mb-3 bg-light">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Sous-total :</span>
                  <span>{formatCurrency(selectedSale.subtotal || 0)}</span>
                </div>

                {selectedSale.discount > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-danger">
                    <span>Remise :</span>
                    <span>-{formatCurrency(selectedSale.discount)}</span>
                  </div>
                )}

                {selectedSale.tax > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Taxe :</span>
                    <span>{formatCurrency(selectedSale.tax || 0)}</span>
                  </div>
                )}

                <hr />
                <div className="d-flex justify-content-between">
                  <h5 className="mb-0">Total :</h5>
                  <h5 className="mb-0 text-success">
                    {formatCurrency(selectedSale.total || 0)}
                  </h5>
                </div>
              </div>

              {/* ========== SECTION 5 : NOTES (optionnel) ========== */}
              {selectedSale.notes && (
                <div className="border rounded-3 p-3 bg-light">
                  <h6 className="d-flex align-items-center mb-2">
                    <FileText size={18} className="me-2 text-muted" />
                    Notes
                  </h6>
                  <p className="mb-0 text-muted small">{selectedSale.notes}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        {/* ========== FOOTER : ACTIONS ========== */}
        <Modal.Footer className="border-0 p-3 p-md-4 bg-light">
          <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-sm-auto">
            <Button
              variant="secondary"
              onClick={() => setShowDetailModal(false)}
              className="order-4 order-sm-1"
            >
              Fermer
            </Button>
            <Button
              variant="outline-info"
              onClick={() => selectedSale && handlePreviewSale(selectedSale)}
              className="order-3 order-sm-2 d-flex align-items-center justify-content-center"
            >
              <Eye size={16} className="me-1" />
              <span className="d-none d-sm-inline">Aperçu ticket</span>
              <span className="d-sm-none">Aperçu</span>
            </Button>
            <Button
              variant="success"
              onClick={() => selectedSale && handlePrintSale(selectedSale)}
              disabled={printing === selectedSale?.id}
              className="order-2 order-sm-3 d-flex align-items-center justify-content-center"
            >
              {printing === selectedSale?.id ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Printer size={16} className="me-1" />
              )}
              <span className="d-none d-sm-inline">Imprimer ticket</span>
              <span className="d-sm-none">Imprimer</span>
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* AJOUTER le Modal d'annulation à la place */}
      <Modal
        show={showCancelModal}
        onHide={() => {
          setShowCancelModal(false);
          setSaleToCancel(null);
          setCancelReason("");
        }}
        centered
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <AlertCircle size={24} className="me-2" />
            Annuler la vente
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {saleToCancel && (
            <>
              <Alert variant="warning" className="mb-3">
                <AlertCircle size={18} className="me-2" />
                <strong>Attention :</strong> Cette action est irréversible !
              </Alert>

              <Card className="mb-3 border-danger">
                <Card.Body>
                  <h6 className="mb-2">Informations de la vente :</h6>
                  <div className="small">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Numéro :</span>
                      <strong>{saleToCancel.saleNumber}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Client :</span>
                      <strong>
                        {saleToCancel.customer?.name || "Client anonyme"}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Montant :</span>
                      <strong className="text-danger">
                        {formatCurrency(saleToCancel.total)}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Date :</span>
                      <strong>{formatDate(saleToCancel.createdAt)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Articles :</span>
                      <strong>
                        {saleToCancel.items?.length || 0} article(s)
                      </strong>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Alert variant="info" className="mb-3">
                <Clock size={18} className="me-2" />
                Temps restant pour annuler :{" "}
                <strong>{Math.round(getHoursRemaining(saleToCancel))}h</strong>
              </Alert>

              <h6 className="mb-2">Conséquences de l'annulation :</h6>
              <ul className="small text-muted mb-3">
                <li>Le stock des produits sera rétabli</li>
                <li>Le montant sera déduit du total des achats du client</li>
                <li>La vente sera définitivement supprimée</li>
                <li>Cette action ne peut pas être annulée</li>
              </ul>

              <Form.Group>
                <Form.Label>
                  <strong className="text-danger">
                    * Raison de l'annulation :
                  </strong>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Indiquez la raison de l'annulation (obligatoire)..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">
                  Exemple : Erreur de saisie, demande du client, produit
                  défectueux, etc.
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCancelModal(false);
              setSaleToCancel(null);
              setCancelReason("");
            }}
            disabled={cancelling}
          >
            <X size={18} className="me-2" />
            Fermer
          </Button>
          <Button
            variant="danger"
            onClick={confirmCancelSale}
            disabled={cancelling || !cancelReason.trim()}
          >
            {cancelling ? (
              <>
                <Spinner size="sm" className="me-2" />
                Annulation...
              </>
            ) : (
              <>
                <AlertCircle size={18} className="me-2" />
                Confirmer l'annulation
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SalesList;